import type { NetworkErrorType } from './networkErrorType';

/**
 * All known reasons a request can be unauthorized.
 * This array is the single source of truth — the type is derived from it so
 * adding a new value here automatically extends both the type and the guard.
 */
const UNAUTHORIZED_REASONS = [
  'no-session',
  'session-expired',
  'invalid-session',
  'invalid-token',
  'unknown',
] as const;

/**
 * Machine-readable reason describing *why* a request was unauthorized.
 * Lets the client react precisely (e.g. auto sign-out on session expiry) and
 * lets the server log a specific cause instead of a generic "Unauthorized".
 */
export type UnauthorizedReason = (typeof UNAUTHORIZED_REASONS)[number];

/**
 * Optional fields attached to {@link HttpError} (validation details, etc.).
 */
export type HttpErrorExtraFields = {
  missing?: Record<string, string>;
  duplicated?: Record<string, string>[];
  forbidden?: Record<string, string>;
};

export type HttpErrorOptions = HttpErrorExtraFields & {
  isAutoRetryable?: boolean;
  /** Classified network failure type for consistent client UX. */
  networkErrorType?: NetworkErrorType;
  /** Correlates client errors with server logs (`generateApiError` / `X-Request-Id`). */
  requestId?: string;
  /** Specific cause for 401 responses, surfaced to the client and server logs. */
  reason?: UnauthorizedReason;
};

function readPlainStringRecord(
  value: unknown,
): Record<string, string> | undefined {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, string>;
  }
  return undefined;
}

function readUnauthorizedReason(
  value: unknown,
): UnauthorizedReason | undefined {
  if (
    typeof value === 'string' &&
    (UNAUTHORIZED_REASONS as readonly string[]).includes(value)
  ) {
    return value as UnauthorizedReason;
  }
  return undefined;
}

function readHttpErrorOptionsFromPlainObject(
  obj: Record<string, unknown>,
): HttpErrorOptions | undefined {
  const options: HttpErrorOptions = {};

  const missing = readPlainStringRecord(obj['missing']);
  if (missing) {
    options.missing = missing;
  }

  if (Array.isArray(obj['duplicated'])) {
    options.duplicated = obj['duplicated'] as Record<string, string>[];
  }

  const forbidden = readPlainStringRecord(obj['forbidden']);
  if (forbidden) {
    options.forbidden = forbidden;
  }

  if (typeof obj['isAutoRetryable'] === 'boolean') {
    options.isAutoRetryable = obj['isAutoRetryable'];
  }

  if (
    typeof obj['networkErrorType'] === 'string' &&
    obj['networkErrorType'].length > 0
  ) {
    options.networkErrorType = obj['networkErrorType'] as NetworkErrorType;
  }

  if (typeof obj['requestId'] === 'string' && obj['requestId'].length > 0) {
    options.requestId = obj['requestId'];
  }

  const reason = readUnauthorizedReason(obj['reason']);
  if (reason) {
    options.reason = reason;
  }

  return Object.keys(options).length > 0 ? options : undefined;
}

/**
 * HTTP-layer error used across API routes and the axios client.
 * Always an {@link Error} instance so `instanceof Error` and Sentry reporting behave correctly.
 */
export class HttpError extends Error {
  readonly code: number;
  readonly isAutoRetryable?: boolean;
  readonly networkErrorType?: NetworkErrorType;
  readonly requestId?: string;
  readonly missing?: Record<string, string>;
  readonly duplicated?: Record<string, string>[];
  readonly forbidden?: Record<string, string>;
  readonly reason?: UnauthorizedReason;

  constructor(
    code: number,
    message: string,
    name: string,
    options?: HttpErrorOptions,
  ) {
    super(message);
    this.name = name;
    this.code = code;
    if (options) {
      if (options.duplicated !== undefined) {
        this.duplicated = options.duplicated;
      }
      if (options.forbidden !== undefined) {
        this.forbidden = options.forbidden;
      }
      if (options.missing !== undefined) {
        this.missing = options.missing;
      }
      if (options.isAutoRetryable !== undefined) {
        this.isAutoRetryable = options.isAutoRetryable;
      }
      if (options.networkErrorType !== undefined) {
        this.networkErrorType = options.networkErrorType;
      }
      if (options.requestId !== undefined) {
        this.requestId = options.requestId;
      }
      if (options.reason !== undefined) {
        this.reason = options.reason;
      }
    }
    Object.setPrototypeOf(this, new.target.prototype);
  }

  /**
   * Shape sent in JSON bodies (`generateApiError`); required because `JSON.stringify` does not
   * serialize {@link Error} properties reliably by default.
   */
  toJSON(): {
    code: number;
    name: string;
    message: string;
    isAutoRetryable?: boolean;
    networkErrorType?: NetworkErrorType;
    requestId?: string;
    reason?: UnauthorizedReason;
    missing?: Record<string, string>;
    duplicated?: Record<string, string>[];
    forbidden?: Record<string, string>;
  } {
    return {
      code: this.code,
      name: this.name,
      message: this.message,
      ...(this.isAutoRetryable !== undefined && {
        isAutoRetryable: this.isAutoRetryable,
      }),
      ...(this.networkErrorType !== undefined && {
        networkErrorType: this.networkErrorType,
      }),
      ...(this.requestId !== undefined && { requestId: this.requestId }),
      ...(this.reason !== undefined && { reason: this.reason }),
      ...(this.missing !== undefined && { missing: this.missing }),
      ...(this.duplicated !== undefined && { duplicated: this.duplicated }),
      ...(this.forbidden !== undefined && { forbidden: this.forbidden }),
    };
  }

  /**
   * Rehydrates a structured API error (e.g. JSON from `error.response.data`) into an {@link HttpError}.
   *
   * @param obj - Parsed JSON error body (InSafe `{ code: number }`, SAP `{ code: "401" }`, etc.)
   * @param parseOptions - When `httpStatusFallback` is set, missing or non-numeric codes use that
   *   HTTP status instead of returning `InvalidErrorResponse`.
   */
  static fromPlainObject(
    obj: Record<string, unknown>,
    parseOptions?: { httpStatusFallback?: number },
  ): HttpError {
    if (obj instanceof HttpError) {
      return obj;
    }

    const rawCode = obj['code'];
    const rawMessage = obj['message'];
    const defaultName = 'APIError';

    if (!hasPresentApiErrorCode(rawCode)) {
      if (parseOptions?.httpStatusFallback !== undefined) {
        return new HttpError(
          parseOptions.httpStatusFallback,
          messageFromPlainObject(rawMessage),
          resolveErrorNameFromPlainObject(obj, rawCode, defaultName),
        );
      }

      return new HttpError(
        500,
        'Invalid API error response: missing or invalid code',
        'InvalidErrorResponse',
      );
    }

    const httpStatus = resolveHttpStatusFromApiCode(
      rawCode,
      parseOptions?.httpStatusFallback ?? 500,
    );
    const name = resolveErrorNameFromPlainObject(obj, rawCode, defaultName);
    const message = messageFromPlainObject(rawMessage);

    return new HttpError(
      httpStatus,
      message,
      name,
      readHttpErrorOptionsFromPlainObject(obj),
    );
  }
}

export type HttpErrorWithRequestId = HttpError & {
  readonly requestId: string;
};

/**
 * Returns a copy of an HTTP error with a request id for server/client log correlation.
 */
export function withHttpErrorRequestId(
  error: HttpError,
  requestId = globalThis.crypto.randomUUID(),
): HttpErrorWithRequestId {
  const resolvedRequestId = error.requestId ?? requestId;

  return new HttpError(error.code, error.message, error.name, {
    ...(error.duplicated !== undefined && { duplicated: error.duplicated }),
    ...(error.forbidden !== undefined && { forbidden: error.forbidden }),
    ...(error.missing !== undefined && { missing: error.missing }),
    ...(error.isAutoRetryable !== undefined && {
      isAutoRetryable: error.isAutoRetryable,
    }),
    ...(error.networkErrorType !== undefined && {
      networkErrorType: error.networkErrorType,
    }),
    ...(error.reason !== undefined && { reason: error.reason }),
    requestId: resolvedRequestId,
  }) as HttpErrorWithRequestId;
}

/** Type guard that narrows an unknown value to a known {@link UnauthorizedReason}. */
export function isUnauthorizedReason(
  value: unknown,
): value is UnauthorizedReason {
  return (
    typeof value === 'string' &&
    (UNAUTHORIZED_REASONS as readonly string[]).includes(value)
  );
}

/**
 * Returns true when an API error payload includes a non-empty `code` field.
 * Accepts numbers and strings (e.g. SAP `"401"` or InSafe `422`).
 */
export function hasPresentApiErrorCode(value: unknown): boolean {
  if (value == null) {
    return false;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return false;
}

/**
 * Maps an API `code` to an HTTP status for {@link HttpError}.
 * Numeric and numeric-string codes in the 100–599 range are used as-is;
 * non-numeric strings (e.g. `VALIDATION_ERROR`) fall back to `httpStatusFallback`.
 */
export function resolveHttpStatusFromApiCode(
  code: unknown,
  httpStatusFallback: number,
): number {
  if (typeof code === 'number' && Number.isFinite(code)) {
    if (code >= 100 && code <= 599) {
      return code;
    }
    return httpStatusFallback;
  }

  if (typeof code === 'string') {
    const trimmed = code.trim();
    if (/^\d+$/.test(trimmed)) {
      const parsed = Number.parseInt(trimmed, 10);
      if (parsed >= 100 && parsed <= 599) {
        return parsed;
      }
    }
  }

  return httpStatusFallback;
}

function resolveErrorNameFromPlainObject(
  obj: Record<string, unknown>,
  rawCode: unknown,
  defaultName: string,
): string {
  if (typeof obj['name'] === 'string' && obj['name'].length > 0) {
    return obj['name'];
  }
  if (typeof obj['name'] === 'number') {
    return String(obj['name']);
  }
  if (typeof rawCode === 'string') {
    const trimmed = rawCode.trim();
    if (trimmed.length > 0 && !/^\d+$/.test(trimmed)) {
      return trimmed;
    }
  }
  return defaultName;
}

function messageFromPlainObject(rawMessage: unknown): string {
  if (typeof rawMessage === 'string') {
    return rawMessage;
  }
  if (rawMessage == null) {
    return 'Unknown error';
  }
  return String(rawMessage);
}

/** Alias kept for existing imports; prefer {@link HttpError}. */
export type CustomErrorType = HttpError;

const createHttpError = (
  code: number,
  message: string,
  name: string,
  options?: HttpErrorOptions,
): HttpError => {
  return new HttpError(code, message, name, options);
};

type ErrorTypes =
  | 'badRequest'
  | 'unauthorized'
  | 'forbidden'
  | 'notFound'
  | 'internalError';

type HTTPErrorsType = Record<
  ErrorTypes,
  (
    message?: string,
    name?: string,
    extraInformation?: HttpErrorOptions,
  ) => HttpError
> & {
  custom: (
    code: number,
    message: string,
    name: string,
    options?: HttpErrorOptions,
  ) => HttpError;
};

export const HTTPErrors: HTTPErrorsType = {
  badRequest: (
    message = 'Bad request',
    name = 'Bad request',
    extraInformation = {},
  ) => createHttpError(400, message, name, extraInformation),
  unauthorized: (
    message = 'Unauthorized',
    name = 'Unauthorized',
    extraInformation,
  ) => createHttpError(401, message, name, extraInformation),
  forbidden: (message = 'Forbidden', name = 'Forbidden') =>
    createHttpError(403, message, name),
  notFound: (message = 'Not found', name = 'Not found') =>
    createHttpError(404, message, name),
  internalError: (
    message = 'An internal server error has occurred',
    name = 'InternalError',
    options?: HttpErrorOptions,
  ) => createHttpError(500, message, name, options),
  custom: (
    code: number,
    message: string,
    name: string,
    options?: HttpErrorOptions,
  ) => createHttpError(code, message, name, options),
};
